import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowUpRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  PartyPopper,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { paths } from "@/routes/path";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { loginSchema, formRateLimiter } from "@/utils/formSecurity";

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const from = paths.app.dashboard.absolute;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      clearError();

      if (!formRateLimiter.canSubmit("login")) {
        throw new Error("Too many login attempts. Please try again later.");
      }

      await login(data);
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error("Login error:", err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between space-x-4 text-sm text-slate-600">
        <span>Admission not confirmed?</span>
        <Link
          to={paths.auth.admissionStatus.absolute}
          className="inline-flex items-center gap-2 rounded-full border border-[#CBEED8] bg-[#EAF6EE] px-4 py-1.5 text-xs font-medium text-[#15803D] shadow-sm transition hover:border-[#A7E0BE]"
        >
          <span className="h-2 w-2 rounded-full bg-[#22C55E]" />
          Check admission status
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="rounded-[32px] bg-white p-8 shadow-[0_20px_40px_rgba(15,23,42,0.08)]">
        <div className="mb-8 space-y-2">
          <p className="text-sm font-medium uppercase tracking-[0.2em]">
            Hey! Welcome <PartyPopper className="mb-2 inline h-5 w-5" />
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50/70 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-slate-700">
              Email Address
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#7B2A40] text-white">
                <Mail className="h-5 w-5" />
              </span>
              <Input
                {...register("email")}
                id="email"
                type="email"
                autoComplete="email"
                placeholder="olivia@untitledui.com"
                className={cn(
                  errors.email &&
                    "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                )}
              />
            </div>
            {errors.email && (
              <p className="text-xs text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="password"
              className="text-sm font-medium text-slate-700"
            >
              Password
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#7B2A40] text-white">
                <Lock className="h-5 w-5" />
              </span>
              <Input
                {...register("password")}
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="********"
                className={cn(
                  errors.password &&
                    "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                )}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-[#7B2A40] transition hover:bg-[#F8E8ED]"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
              />
            </div>
            {errors.password && (
              <p className="text-xs text-red-600">{errors.password.message}</p>
            )}
          </div>

          <div className="flex justify-end">
            <Link
              to={paths.auth.forgotPassword.absolute}
              className="text-sm text-[#7B2A40] underline"
            >
              Forgotten Password
            </Link>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Signing in...
              </span>
          ) : (
            "Log in"
          )}
        </Button>
      </form>
    </div>

      <div className="flex items-center justify-between gap-3 rounded-2xl bg-[#F9F5FF] px-4 py-3 text-sm shadow-sm">
        <div className="flex flex-col">
          <span className="font-semibold">New student?</span>
          <span className="text-xs">
            Start your application to join Kaduna Business School.
          </span>
        </div>
        <Link
          to={paths.public.apply.absolute}
          className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#7B2A40] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md shrink-0"
        >
          Apply now
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      {/* <div className="space-y-6">
        <div className="relative flex items-center justify-center">
          <span className="absolute left-0 h-px w-full bg-[#E4E7EC]" />
          <span className="relative bg-[#F7F8FD] px-3 text-xs tracking-[0.3em] text-[#98A2B3]">
            or
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Button
            type="button"
            variant="outline"
            className="h-12 w-full justify-center rounded-xl border border-[#E4E7EC] bg-white text-sm font-semibold text-slate-700 hover:bg-[#F2F4F7]"
          >
            <span className="flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 48 48"
                  className="h-5 w-5"
                >
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.28 0 5.45 1.42 6.7 2.6l4.89-4.89C32.12 4.02 28.37 2 24 2 14.82 2 7.26 7.97 4.27 16.09l5.68 4.41C11.59 14.43 17.26 9.5 24 9.5z"
                  />
                  <path
                    fill="#34A853"
                    d="M46.5 24.5c0-1.57-.14-3.08-.41-4.5H24v8.52h12.65c-.55 2.82-2.24 5.21-4.78 6.82l7.38 5.73C43.79 37.49 46.5 31.49 46.5 24.5z"
                  />
                  <path
                    fill="#4A90E2"
                    d="M9.95 28.5c-.45-1.33-.7-2.75-.7-4.25s.25-2.92.7-4.25l-5.68-4.41C2.58 18.78 2 21.33 2 24c0 2.67.58 5.22 1.62 7.41l6.33-2.91z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M24 46c6.48 0 11.92-2.14 15.89-5.82l-7.38-5.73C30.59 36.74 27.59 38 24 38c-6.74 0-12.41-4.93-14.05-11.5l-6.33 2.91C7.26 40.03 14.82 46 24 46z"
                  />
                  <path fill="none" d="M2 2h44v44H2z" />
                </svg>
              </span>
              Sign in with Google
            </span>
          </Button>

          <Button
            type="button"
            variant="outline"
            className="h-12 w-full justify-center rounded-xl border border-[#E4E7EC] bg-white text-sm font-semibold text-slate-700 hover:bg-[#F2F4F7]"
          >
            <span className="flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E0EDFF] text-[#1D4ED8]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path d="M14.857 21.487a1.125 1.125 0 0 1-1.071-.789l-.92-3.052a1.125 1.125 0 0 1 .301-1.119l4.906-4.906a3.375 3.375 0 0 0-4.771-4.771l-4.906 4.906a1.125 1.125 0 0 1-1.119.301l-3.052-.92a1.125 1.125 0 0 1-.789-1.071 9 9 0 1 1 11.421 11.421z" />
                </svg>
              </span>
              Sign in with SSO
            </span>
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-12 w-full justify-center rounded-xl border border-[#E4E7EC] bg-white text-sm font-semibold text-slate-700 hover:bg-[#F2F4F7]"
        >
          <span className="flex items-center gap-3">
            <span className="grid h-6 w-6 grid-cols-2 grid-rows-2 gap-[1px] overflow-hidden rounded-sm">
              <span className="bg-[#F35325]" />
              <span className="bg-[#81BC06]" />
              <span className="bg-[#05A6F0]" />
              <span className="bg-[#FFBA08]" />
            </span>
            Sign in with Microsoft
          </span>
        </Button>
      </div> */}
    </div>
  );
}
